import React, { createContext, useContext, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useValidateLogin } from '../hooks/auth';
import { APP_MODE } from '../utils/constants';
import { useTrialStats } from '../hooks/customer';

const MODE_WHITELIST = {
  genptbeta: ['/app/genpt', '/', '/signup', '/admin', '/verify_code', '/cpanel'],
};

const MODE_REDIRECT = {
  genptbeta: '/app/genpt/overview',
};

const AppModeContext = createContext(null);

export const AppModeProvider = ({ children }) => {
  const IS_GENPT_BETA = APP_MODE === 'genptbeta';

  // Static config
  const allowedPrefixes = MODE_WHITELIST[APP_MODE] || [];
  const redirectUrl = MODE_REDIRECT[APP_MODE] || '/';

  // Hooks always called
  const location = useLocation();
  const navigate = useNavigate();

  const { data: userData } = useValidateLogin({
    enabled: !IS_GENPT_BETA,
  });

  // 👉 Trial API should only run in genptbeta
  const {
    data: trialStats,
    isLoading: isTrialLoading,
    isFetching: isTrialFetching,
    refetch,
  } = useTrialStats({
    enabled: IS_GENPT_BETA && !!userData,
  });

  const currentPath = location.pathname;

  // 👉 Redirects disabled in beta, only apply outside
  const shouldRedirect =
    !IS_GENPT_BETA &&
    APP_MODE &&
    userData?.user?.user_type !== 'provider' &&
    !allowedPrefixes.some(prefix =>
      prefix === '/' ? currentPath === '/' : currentPath.startsWith(prefix)
    );

  useEffect(() => {
    if (shouldRedirect) {
      navigate(redirectUrl, { replace: true });
    }
  }, [shouldRedirect, navigate, redirectUrl]);

  const refreshTrialStats = useCallback(() => {
    if (IS_GENPT_BETA) refetch();
  }, [IS_GENPT_BETA, refetch]);

  const value = useMemo(
    () => ({
      APP_MODE,
      allowedPrefixes,
      redirectUrl,

      // data (only meaningful in beta)
      trialStats: IS_GENPT_BETA ? trialStats : null,
      trialExpired: IS_GENPT_BETA ? trialStats?.is_trial_expired : false,
      dateExpired: IS_GENPT_BETA ? trialStats?.date_expired : false,
      testsExceeded: IS_GENPT_BETA ? trialStats?.tests_exceeded : false,
      attemptedTests: IS_GENPT_BETA ? trialStats?.attempted_tests : 0,
      invited: IS_GENPT_BETA ? trialStats?.invited : 0,
      totalInvites: IS_GENPT_BETA ? trialStats?.total_invites : 0,

      // loading (exposed only in beta)
      isTrialLoading: IS_GENPT_BETA ? (isTrialLoading || isTrialFetching) : false,

      // actions
      refreshTrialStats,
    }),
    [
      IS_GENPT_BETA,
      trialStats,
      allowedPrefixes,
      redirectUrl,
      isTrialLoading,
      isTrialFetching,
      refreshTrialStats,
    ]
  );

  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>;
};

export const useAppMode = () => useContext(AppModeContext);
